/**
 * Make "the onboarding wizard was already shown to this user" survive, whatever
 * happens next.
 *
 * WHAT WAS STILL WRONG (this is the half migration 166 could not fix)
 *
 * Migration 166 gave the wizard the right gate — `onboarding_shown_at`, stamped
 * the instant it renders rather than when it is answered — so refreshing,
 * closing the tab and clicking away are all covered by a write that already
 * happened. Correct, and it stays.
 *
 * But that stamp reached the server as ONE unverified fire-and-forget request:
 *
 *     fetch("/api/user/onboarding", { method: "PATCH", keepalive: true })
 *       .then((r) => { if (!r.ok) console.warn(...) })
 *       .catch(() => {});
 *
 * Nothing retried it and nothing else recorded the impression, so a single lost
 * request put the user straight back into the reported bug: the wizard renders,
 * the PATCH fails (offline for a moment, a 502 from the edge, a 500 because
 * migration 166 has not been applied to that database yet), the user refreshes
 * — and `GET` still answers `shown: false`, so they are asked again. And again.
 * Every symptom in the ticket ("dismiss it, skip it, refresh, or return later")
 * is that one request not landing.
 *
 * The submit `POST` is not the safety net it looks like either: it also stamps
 * shown, but only on "Continue" / "Skip for now". The dismissal paths the ticket
 * actually names — refresh, navigate away — never reach it.
 *
 * WHAT CHANGED SINCE (migration 182) — READ THIS FIRST
 *
 * The layers below were catching a stamp that could be lost. They no longer
 * have to: `claimOnboardingOffer` asks the server to DECIDE and RECORD in one
 * statement, so the client is never told to show the wizard without the server
 * already knowing it did. That is what makes the suppression genuinely
 * cross-device, which is the acceptance criterion the layers below could only
 * approximate — every one of them is device-local or best-effort, so whenever
 * the stamp failed the suppression quietly became "this browser only" and the
 * next browser asked again.
 *
 * Everything after this point is now a BACKSTOP: it covers the beacon path, a
 * client bundle that predates the claim, and the marker that lets a remount skip
 * a round trip. Correctness no longer rests on it. Kept because none of it is
 * wrong and the beacon still has a job — but do not add a fifth layer here; if
 * something is getting through, the claim is where to look.
 *
 * THREE LAYERS, SO NO SINGLE FAILURE RE-ASKS
 *
 *   1. A local marker, written SYNCHRONOUSLY before the request is even made.
 *      Needs no network, so a refresh one second later is already suppressed.
 *      Deliberately a fallback under the server state, never a replacement:
 *      it does not cross devices, which is why layers 2 and 3 exist. (The
 *      acceptance criterion is "server-backed state ... not ONLY local
 *      storage", not "no local storage".)
 *   2. Bounded retries on the stamp itself, so a transient failure costs a
 *      couple of seconds rather than the impression record.
 *   3. If every retry fails the marker stays `pending`, and the next page load
 *      re-sends it — so the server converges and the suppression becomes
 *      cross-device as soon as the user is online again. A `pagehide` beacon
 *      covers leaving before that happens.
 *
 * WHY A MARKER CAN NEVER SUPPRESS A WIZARD NOBODY SAW
 *
 * That is the one dangerous failure here (see verification-gate.ts for the
 * incident), and the rule that prevents it is structural: `markOnboardingShown`
 * is called from exactly the places that call `setShowPersonalization(true)`, in
 * the same breath. Nothing else in the codebase writes the marker. A deferral —
 * which must stay reversible — never calls it, and the source-text tests in
 * wizard-build-guard-placement.test.ts pin that.
 */

import {
  shouldShowOnboardingWizard,
  type OnboardingStatus,
} from "@/lib/onboarding/personalization";

const ENDPOINT = "/api/user/onboarding";

/**
 * User-scoped on purpose. A shared browser is real: without the id in the key,
 * the first person to sign up would suppress the wizard for everyone who signs
 * in on that machine afterwards.
 */
const KEY_PREFIX = "panda:onboarding-shown:";

/**
 * `pending` — shown, but the server has not confirmed it. Suppresses locally and
 *   is re-sent on the next load.
 * `synced` — the server has it. Every device is covered; nothing left to do.
 *
 * Both mean "already shown", so reading is a null check. Only the flush cares
 * which.
 */
type MarkerState = "pending" | "synced";

/** Immediate, ~1s, ~3s. Long enough to outlast a blip, short enough that the
 *  user is usually still on the page for the last attempt. */
const RETRY_DELAYS_MS = [0, 900, 2600];

export interface MarkOnboardingShownOptions {
  /** Overridden by tests so retries do not cost real seconds. */
  retryDelaysMs?: number[];
}

function storageKey(userId: string): string {
  return `${KEY_PREFIX}${userId}`;
}

/**
 * In-memory mirror of the marker, for contexts where localStorage THROWS:
 * Safari private mode, a browser set to block site data, a sandboxed iframe
 * (SecurityError), an exceeded quota.
 *
 * Without it those contexts lost three defences at once, all on the same
 * dependency: `writeMarker` silently no-opped, `hasLocalOnboardingShown` was
 * permanently false so nothing was suppressed locally, `flushPendingOnboardingShown`
 * returned early so the next load never re-sent, and — worst — the `pagehide`
 * handler read back `null`, decided there was nothing outstanding, and never
 * beaconed. Only the ~3.5s in-page retry ladder survived.
 *
 * A page-life mirror does not persist across a reload the way localStorage does,
 * so it is strictly weaker. But it keeps the beacon and the flush honest within
 * the page, which is where they do their work.
 */
const memoryMarkers = new Map<string, MarkerState>();

/**
 * Every localStorage access is wrapped: it throws outright in Safari private
 * mode and whenever a quota is exceeded, and the wizard must not break because
 * a defence-in-depth layer is unavailable. Losing the marker degrades to the
 * server-only behaviour that already exists — never to a crash.
 *
 * Reads prefer the strongest evidence available: either store saying "shown"
 * means shown. They can legitimately disagree — localStorage survives a reload
 * and the mirror does not — and in every disagreement the answer we want is the
 * one that suppresses.
 */
function readMarker(userId: string | null | undefined): MarkerState | null {
  if (!userId || typeof window === "undefined") return null;
  let stored: MarkerState | null = null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (raw === "pending" || raw === "synced") stored = raw;
  } catch {
    // Fall through to the mirror.
  }
  const mirrored = memoryMarkers.get(userId) ?? null;
  // "synced" outranks "pending": it means the server already has it, so the
  // flush and the beacon both have nothing left to do.
  if (stored === "synced" || mirrored === "synced") return "synced";
  return stored ?? mirrored;
}

function writeMarker(userId: string, state: MarkerState): void {
  if (typeof window === "undefined") return;
  // The mirror first, and unconditionally: it is the half that cannot throw, so
  // writing it before the attempt that can means a storage failure costs the
  // durable layer only, never the in-page one.
  memoryMarkers.set(userId, state);
  try {
    window.localStorage.setItem(storageKey(userId), state);
  } catch {
    // Storage unavailable — the mirror above plus layers 2 and 3 carry it.
  }
}

/** Has this browser already put the wizard in front of this user? */
export function hasLocalOnboardingShown(userId: string | null | undefined): boolean {
  return readMarker(userId) !== null;
}

/**
 * THE one-time gate, for callers that have both halves of the answer.
 *
 * Both surfaces must decide identically — surfaces disagreeing about what
 * "already handled" means is how this broke the first time — so the local
 * fallback is folded in here rather than bolted onto each call site.
 * `shouldShowOnboardingWizard` stays a pure function of the server payload and
 * keeps failing closed on a null one.
 */
export function onboardingWizardIsOwed(
  status: OnboardingStatus | null | undefined,
  userId: string | null | undefined,
): boolean {
  return shouldShowOnboardingWizard(status) && !hasLocalOnboardingShown(userId);
}

function sleep(ms: number): Promise<void> {
  return ms > 0 ? new Promise((r) => setTimeout(r, ms)) : Promise.resolve();
}

/**
 * Run `send` on the bounded ladder, stopping at the first success.
 *
 * Exported because the wizard's SUBMIT has the same problem the stamp had and
 * deserves the same treatment rather than a second copy of this loop: a single
 * unverified request whose failure is swallowed, after which the modal closes
 * anyway and the answers the user just typed are gone
 * (`submitPersonalization` in app/home-client.tsx). One ladder, one place to
 * change the delays.
 */
export async function sendWithRetries(
  send: () => Promise<boolean>,
  delaysMs: number[] = RETRY_DELAYS_MS,
): Promise<boolean> {
  for (const delay of delaysMs) {
    await sleep(delay);
    if (await send()) return true;
  }
  return false;
}

/**
 * `keepalive` so an immediate refresh cannot cancel the attempt in flight, and
 * an explicit `!r.ok` check because fetch does not reject on 4xx/5xx — without
 * it a failed stamp is indistinguishable from a successful one, which is the
 * bug in miniature.
 */
async function sendStamp(): Promise<boolean> {
  try {
    const res = await fetch(ENDPOINT, { method: "PATCH", keepalive: true });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * The unload transport. `sendBeacon` is the only request the browser promises to
 * deliver from a page that is going away — a `fetch` started during `pagehide`
 * is routinely dropped even with `keepalive`.
 *
 * It can only issue POST, so the route accepts `{ event: "shown" }` on POST as a
 * synonym for PATCH. That body is deliberately NOT an empty submit: an empty
 * POST is what "Skip for now" sends and would stamp `onboarding_completed_at`,
 * making the completion rate 100% by construction — the exact conflation
 * migration 166 split apart.
 */
function beaconStamp(): boolean {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
    return false;
  }
  try {
    return navigator.sendBeacon(
      ENDPOINT,
      new Blob([JSON.stringify({ event: "shown" })], { type: "application/json" }),
    );
  } catch {
    return false;
  }
}

/**
 * One in-flight stamp per user per page life. Both surfaces can call this (a
 * client-side navigation from home into a project mounts the second one without
 * a reload), and a React StrictMode double-effect calls it twice on its own. The
 * RPC coalesces server-side either way; this just avoids the duplicate traffic.
 *
 * Holds the PROMISE, not just the id. A second caller used to get a bare `false`
 * and no send — indistinguishable from "every attempt failed", and a silent
 * no-op for any future call site that stamped while a flush was running. Sharing
 * the promise means every caller gets the real outcome of the one request that
 * is actually being made.
 */
const inFlight = new Map<string, Promise<boolean>>();

/**
 * Registered lazily — a user who never sees the wizard carries no unload
 * listener at all — and then left in place for the page life. Removing it once
 * the stamp syncs would mean re-adding it if a second surface stamps later,
 * and getting THAT wrong duplicates the listener. The handler re-reads the
 * marker instead, so a listener with nothing to send costs one storage read.
 *
 * Values are CLEANUP closures, not the handlers themselves: each entry now
 * registers two listeners (`pagehide` + `visibilitychange`) and only the
 * closure that added them knows how to take both off again.
 */
const unloadGuarded = new Map<string, () => void>();

function guardUnload(userId: string): void {
  if (typeof window === "undefined" || unloadGuarded.has(userId)) return;
  const onHide = () => {
    // Re-read rather than closing over the state: by the time the user leaves,
    // a retry has usually landed and there is nothing left to send. `readMarker`
    // consults the in-memory mirror too, so this still fires in a context where
    // localStorage throws — which is exactly the context where the beacon is the
    // only layer left.
    if (readMarker(userId) !== "pending") return;
    beaconStamp();
  };
  // `pagehide` rather than `unload`: `unload` is skipped for bfcache-eligible
  // navigations and deprecated in Chrome, so it misses the most common way of
  // leaving a page.
  //
  // Plus `visibilitychange`, because `pagehide` is NOT fired when iOS Safari
  // kills a backgrounded tab — the single most common way a mobile session
  // ends. Both handlers are the same function and the RPC coalesces, so firing
  // on both is at worst one redundant beacon.
  const onVisibility = () => {
    if (document.visibilityState === "hidden") onHide();
  };
  unloadGuarded.set(userId, () => {
    window.removeEventListener("pagehide", onHide);
    document.removeEventListener("visibilitychange", onVisibility);
  });
  window.addEventListener("pagehide", onHide);
  document.addEventListener("visibilitychange", onVisibility);
}

/**
 * Record — locally and then durably on the server — that the wizard has just
 * been put on screen for this user.
 *
 * Call it at the same instant as `setShowPersonalization(true)`, never earlier
 * and never on a path that only MIGHT render the wizard. Returns once the
 * server has confirmed (or every attempt has failed); callers do not need to
 * await it — the local marker is already written by the time it yields.
 */
export async function markOnboardingShown(
  userId: string | null | undefined,
  options: MarkOnboardingShownOptions = {},
): Promise<boolean> {
  const delays = options.retryDelaysMs ?? RETRY_DELAYS_MS;

  // No id means no marker to key. Still stamp the server — the route derives the
  // user from the session cookie, not from anything the client sends — we just
  // lose the local fallback for this call. It gets the retry ladder anyway: this
  // branch used to be a single unverified request, i.e. the exact fire-and-forget
  // pattern this whole module exists to replace, and it is the branch with the
  // FEWEST other layers behind it — no marker to suppress locally, nothing for
  // flushPendingOnboardingShown to recover, no beacon on the way out. One lost
  // request there is the reappearance bug with no safety net at all.
  if (!userId) return sendWithRetries(sendStamp, delays);

  if (readMarker(userId) === "synced") return true;

  // Synchronous, and FIRST: a refresh a moment from now must already be
  // suppressed, whatever the network does below.
  writeMarker(userId, "pending");
  guardUnload(userId);

  // Someone is already sending this exact stamp — await their result rather
  // than starting a second request or returning a `false` that means nothing.
  const existing = inFlight.get(userId);
  if (existing) return existing;

  const attempt = (async () => {
    if (await sendWithRetries(sendStamp, delays)) {
      writeMarker(userId, "synced");
      return true;
    }
    // Still `pending`. The next load re-sends it (flushPendingOnboardingShown),
    // and `pagehide` / `visibilitychange` beacon it if the user leaves first.
    return false;
  })().finally(() => {
    inFlight.delete(userId);
  });

  inFlight.set(userId, attempt);
  return attempt;
}

/** What the server said when we asked to claim the one offer this user gets. */
export interface OnboardingClaim {
  /** Show the wizard. True at most once per user, ever — the server has already
   *  recorded the impression by the time this is read. */
  owed: boolean;
  theme: string | null;
  role: string | null;
  companySize: string | null;
}

/**
 * THE way the wizard opens (migration 182).
 *
 * Everything else in this module is a net under a stamp that might not land.
 * This removes the thing they were catching: the server decides "owed" and
 * writes `onboarding_shown_at` in one statement, so there is no interval in
 * which the wizard has been shown but the server does not know it. Two tabs,
 * two devices and a refresh mid-decision all resolve to exactly one `owed`.
 *
 * Fails CLOSED, like every other gate here: a non-ok response, a network error
 * or an unparsable body means "do not show". A wizard skipped this load is
 * offered again on the next one; a wizard shown twice is the ticket.
 *
 * Call it ONLY once every other gate has passed — in particular never while the
 * email-verification gate is pending, because claiming is irreversible and that
 * gate paints over the wizard (see ./verification-gate).
 */
export async function claimOnboardingOffer(
  userId: string | null | undefined,
): Promise<OnboardingClaim> {
  const closed: OnboardingClaim = {
    owed: false,
    theme: null,
    role: null,
    companySize: null,
  };
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // keepalive: the claim is the last thing that happens before the wizard
      // paints, and a navigation right then must not cancel a write the server
      // has already committed to.
      keepalive: true,
      body: JSON.stringify({ event: "claim" }),
      cache: "no-store",
    });
    if (!res.ok) return closed;
    const body = (await res.json()) as Partial<OnboardingClaim> | null;
    if (!body || typeof body !== "object") return closed;

    // Record it locally either way. On `owed` this is the optimisation the
    // marker is now reduced to — a remount can skip the round trip. On
    // `!owed` it stops us re-asking the server on every client-side nav back
    // to home. Either way the durable record is already written server-side,
    // which is the whole point.
    if (userId) writeMarker(userId, "synced");

    return {
      owed: body.owed === true,
      theme: typeof body.theme === "string" ? body.theme : null,
      role: typeof body.role === "string" ? body.role : null,
      companySize: typeof body.companySize === "string" ? body.companySize : null,
    };
  } catch {
    return closed;
  }
}

/**
 * Called on every load, right after the status `GET` resolves: if this browser
 * stamped the wizard but never got the server to agree, tell it now.
 *
 * This is what turns the local marker from "suppressed on this device" into the
 * cross-device, cross-session suppression the ticket asks for — the user simply
 * has to come back online once.
 *
 * `serverShown` short-circuits the common case where the original stamp did
 * land and only the confirmation was lost, so a healthy session costs zero
 * extra requests.
 */
export async function flushPendingOnboardingShown(
  userId: string | null | undefined,
  serverShown: boolean,
  options: MarkOnboardingShownOptions = {},
): Promise<void> {
  if (!userId || readMarker(userId) !== "pending") return;

  if (serverShown) {
    writeMarker(userId, "synced");
    return;
  }

  await markOnboardingShown(userId, options);
}

/**
 * Test seam. Module-level state is page-life state — in a browser it dies with
 * the window — so it has to be unwound explicitly between cases, listeners
 * included: a leaked `pagehide` handler beacons on behalf of an earlier test.
 */
export function __resetOnboardingMarkerState(): void {
  inFlight.clear();
  memoryMarkers.clear();
  if (typeof window !== "undefined") {
    // Each entry is its own cleanup closure — it removes both the `pagehide`
    // and the `visibilitychange` listener it registered.
    for (const cleanup of unloadGuarded.values()) cleanup();
  }
  unloadGuarded.clear();
}
